import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { BookingWindowUnit, OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { DeskBookingWindowSetting } from "../../advance-hot-desking/DeskBookingWindowSetting";
import { Switch } from '@shared/ui/atoms/Switch';
import { Accordian } from '@shared/animations/AnimationVariants';
import TextField from '@shared/ui/atoms/TextField';
import OfficeFloorsListControl from "../../advance-hot-desking/components/OfficeFloorsListControl";
import { canUserCreateOffices, canUserEditOfficesInManagement, isCompanyAdminUser } from '@shared/utils/AccessControl';
import Colours from '@shared/ui/atoms/Colours';
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { SpaceBetweenRow } from "../../StyleComponents";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import {
  createOrUpdateOfficeLayout,
  selectActiveOfficeManagementPanel,
  selectFloors,
  selectFocusedChildOffice,
  selectIsLoading
} from '@features/desks/store/editOfficeLayout.duck';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { DeskManagementPanel } from "./DeskManagementPanel";
import { OfficeAreaManagementPanel } from "./OfficeAreaManagementPanel";
import { OfficeManagementPanelView } from "../data/OfficeManagement.model";

export function DeskBookingPanel(props: Props) {
  const office = props.office;
  const currentUser = useSelector(selectCurrentUser);
  const {t} = useTranslation();
  const [officeBookingWindow, setOfficeBookingWindow] = useState(0);
  const [restrictBookingWindowForTeamAdmins, setRestrictBookingWindowForTeamAdmins] = useState(false);
  const [capacityEnabled, setCapacityEnabled] = useState(false);
  const [capacity, setCapacity] = useState<string | null>(null);
  const [bookingWindowUnit, setBookingWindowUnit] = useState<BookingWindowUnit>('WEEKS');
  const [bookingWindowOpenDay, setBookingWindowOpenDay] = useState(1);
  const [bookingWindowOpenTime, setBookingWindowOpenTime] = useState('00:00');
  const [bookingWindowTimezone, setBookingWindowTimezone] = useState('UTC');
  const activeOfficeManagementPanel = useSelector(selectActiveOfficeManagementPanel);
  const focusedChildOffice = useSelector(selectFocusedChildOffice);
  const isLoading = useSelector(selectIsLoading);
  const floors = useSelector(selectFloors)
  const dispatch = useDispatch();

  const showOfficeCapacity = useMemo(() => floors && floors.length < 1, [floors]);

  useEffect(() => {
    setCapacityEnabled(!!office.capacityEnabled);
    setCapacity(office?.capacity ? office.capacity.toString() : null);
    setOfficeBookingWindow(office?.officeBookingWindowWeeks ?? 0);
    setRestrictBookingWindowForTeamAdmins(office.restrictBookingWindowForTeamAdmins);
    setBookingWindowUnit(office?.bookingWindowUnit ?? 'WEEKS');
    setBookingWindowOpenDay(office?.bookingWindowOpenDay ?? 1);
    setBookingWindowOpenTime(office?.bookingWindowOpenTime ?? '00:00');
    setBookingWindowTimezone(office?.bookingWindowTimezone ?? 'UTC');
  }, [office]);

  const onSaveClicked = async () => {
    dispatch(createOrUpdateOfficeLayout({
      ...(office || {}),
      capacity: !!capacity ? parseInt(capacity) : null,
      capacityEnabled: capacityEnabled,
      officeBookingWindowWeeks: officeBookingWindow,
      bookingWindowUnit: bookingWindowUnit,
      restrictBookingWindowForTeamAdmins: restrictBookingWindowForTeamAdmins,
      bookingWindowOpenDay: bookingWindowOpenDay,
      bookingWindowOpenTime: bookingWindowOpenTime,
      bookingWindowTimezone: bookingWindowTimezone,
    }))
  }

  const onDeleteClicked = async () => {
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.DeleteOfficeEntityDialog,
      payload: { officeId: office.id },
    }));
  }

  if (activeOfficeManagementPanel === OfficeManagementPanelView.DeskManagement && !!focusedChildOffice) {
    return <DeskManagementPanel office={focusedChildOffice} />
  } else if (activeOfficeManagementPanel === OfficeManagementPanelView.AreaManagement && !!focusedChildOffice) {
    return <OfficeAreaManagementPanel office={focusedChildOffice} />
  }

  return (
    <Container>
      <HeadlineMedium style={{marginBottom: 24}}>{t('settings.desk-booking')}</HeadlineMedium>
      <FlatContentCard style={{marginBottom: 48}}>
        <BodyRegular weight={600}>{t('general.how-it-works')}</BodyRegular>
        <BodyRegular>{t('settings.desk-bookings-how-it-works')}</BodyRegular>
      </FlatContentCard>

      <HorizontalSpacer />

      <Switch value={capacityEnabled}
              weight={600}
              label={"textinput.enable-desk-booking"}
              style={{marginBottom: 12}}
              disabled={isLoading}
              onChange={(val) => setCapacityEnabled(val)} />

      <HorizontalSpacer />

      <DeskBookingWindowSetting office={office} disabled={isLoading}
                                onOfficeBookingWindowChanged={setOfficeBookingWindow}
                                onBookingWindowUnitChanged={setBookingWindowUnit}
                                onRestrictBookingWindowForTeamAdminsChange={setRestrictBookingWindowForTeamAdmins}
                                onBookingWindowOpenDayChanged={setBookingWindowOpenDay}
                                onBookingWindowOpenTimeChanged={setBookingWindowOpenTime}
                                onBookingWindowTimezoneChanged={setBookingWindowTimezone}/>

      <Accordian isOpen={capacityEnabled}>
        {showOfficeCapacity && <TextField onChange={setCapacity}
                                          label="textinput.desks-available"
                                          type="number"
                                          disabled={isLoading}
                                          dataTest="office-capacity-input"
                                          value={capacity || "0"}
                                          fullWidth={true}
        />}



        {office && <OfficeFloorsListControl office={office}
                                            canCreateOffices={canUserCreateOffices(currentUser)}
                                            isEditable={canUserEditOfficesInManagement(currentUser)} />}
      </Accordian>

      <Accordian isOpen={!capacityEnabled}>
        <FlatContentCard>
          <BodyRegular weight={400} colour={Colours.blue}>{t('settings-page.how-desk-capacities-work-title')}</BodyRegular>
          <BodyRegular><Trans i18nKey={'settings-page.how-desk-capacities-work'} components={[<a href={'mailto:hello@team-today.com'}>hello@team-today.com</a>]} /></BodyRegular>
        </FlatContentCard>
      </Accordian>

      {isCompanyAdminUser(currentUser) && office.floorPlanEnabled && !!(office.officeFloorPlan || office.officePlan) &&
        <OutlineButton click={() => window.location.href = `/edit-floor-plan?officeId=${office.id}`}
                       text={'floor-plan-editor.open-editor'}
                       dataTest={'edit-floor-plan-button'}
                       style={{marginTop: 32}} />}

      <SpaceBetweenRow style={{marginTop: 32}}>
        <PrimaryButton click={onSaveClicked} text={'button.save-changes'} disabled={isLoading} />
        <OutlineButton click={onDeleteClicked} text={'button.delete-office'} disabled={isLoading} />
      </SpaceBetweenRow>

    </Container>
  )
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div`
  
`
